import type {
  NotificationPreferenceUpdate,
  NotificationPreferencesData,
  NotificationTypePreference,
} from '../types/notifications'

/** Watchlist master type row for PATCH (subtype null). */
export function watchlistMasterType(): string {
  return 'watchlist_entity_change'
}

export const WATCHLIST_MASTER_LABEL = 'Slå notifikationer fra/til'

export const SYSTEM_ANNOUNCEMENT_TYPE = 'system_announcement'

export function isWatchlistSubtypeForEntity(
  subtype: string,
  entityType: 'company' | 'person'
): boolean {
  if (subtype.startsWith('company_')) return entityType === 'company'
  if (subtype.startsWith('person_')) return entityType === 'person'
  return false
}

/** Filter preference tree for global vs per-entity editors. */
export function filterPreferencesForScope(
  data: NotificationPreferencesData,
  scope: 'user' | 'item',
  entityType?: 'company' | 'person'
): NotificationPreferencesData {
  let types = data.types

  if (scope === 'item') {
    types = types
      .filter((t) => t.type !== SYSTEM_ANNOUNCEMENT_TYPE)
      .map((t) => {
        if (t.type !== watchlistMasterType()) return t
        return {
          ...t,
          label: WATCHLIST_MASTER_LABEL,
          subtypes: entityType
            ? t.subtypes.filter((s) => isWatchlistSubtypeForEntity(s.subtype, entityType))
            : t.subtypes,
        }
      })
  } else {
    types = types.map((t) =>
      t.type === watchlistMasterType() ? { ...t, label: WATCHLIST_MASTER_LABEL } : t
    )
  }

  return { types }
}

export function findWatchlistType(
  data: NotificationPreferencesData
): NotificationTypePreference | undefined {
  return data.types.find((t) => t.type === watchlistMasterType())
}

export function isWatchlistMasterEnabled(data: NotificationPreferencesData): boolean {
  return findWatchlistType(data)?.enabled ?? true
}

export function userMasterUpdate(enabled: boolean): NotificationPreferenceUpdate {
  return { type: watchlistMasterType(), subtype: null, enabled }
}

export function userSubtypeUpdate(
  subtype: string,
  enabled: boolean
): NotificationPreferenceUpdate {
  return { type: watchlistMasterType(), subtype, enabled }
}

export function itemMasterUpdate(enabled: boolean | null): NotificationPreferenceUpdate {
  return { type: watchlistMasterType(), subtype: null, enabled }
}

export function itemSubtypeUpdate(
  subtype: string,
  enabled: boolean | null
): NotificationPreferenceUpdate {
  return { type: watchlistMasterType(), subtype, enabled }
}

/** Apply a single preference update to local UI state (optimistic). */
export function applyPreferenceUpdate(
  data: NotificationPreferencesData,
  update: NotificationPreferenceUpdate
): NotificationPreferencesData {
  const typeId = update.type
  const subtype = update.subtype ?? null
  const isMaster = subtype === null || subtype === ''

  return {
    types: data.types.map((typeRow) => {
      if (typeRow.type !== typeId) return typeRow

      if (isMaster) {
        const enabled = update.enabled === true
        return {
          ...typeRow,
          enabled,
          subtypes: typeRow.subtypes.map((sub) => ({
            ...sub,
            enabled: enabled ? sub.enabled : false,
          })),
        }
      }

      const enabled = update.enabled === true
      return {
        ...typeRow,
        subtypes: typeRow.subtypes.map((sub) => {
          if (sub.subtype !== subtype) return sub
          return {
            ...sub,
            enabled,
            itemOverride: update.enabled === null ? null : enabled,
            inheritsUser: update.enabled === null,
          }
        }),
      }
    }),
  }
}
